import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  inMemoryPersistence,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  User,
  UserCredential,
} from 'firebase/auth';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

import { auth } from '../firebase';
import { handleFirebaseError } from '../utils/firebase-errors';

// @ts-ignore
const AuthContext = createContext<{
  user: User | null;
  signup: (email: string, password: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  resetPassword: (email: string) => Promise<void>;
  updatePersistence: (persist: boolean) => Promise<void>;
  logout: () => Promise<void>;
}>();

export const useAuth = () => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = async (email: string, password: string) => {
    try {
      return await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      error = handleFirebaseError(error);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      error = handleFirebaseError(error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      return await sendPasswordResetEmail(auth, email);
    } catch (error) {
      error = handleFirebaseError(error);
      throw error;
    }
  };

  const updatePersistence = async (persist: boolean) => {
    try {
      return await setPersistence(auth, persist ? browserLocalPersistence : inMemoryPersistence);
    } catch (error) {
      error = handleFirebaseError(error);
      throw error;
    }
  };

  const logout = () => {
    return auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        signup,
        login,
        logout,
        resetPassword,
        updatePersistence,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
